import React, { useMemo, useState } from 'react';
import {
    Box, Flex, Text, VStack, HStack, Badge, Button, Icon, Input, Image,
    InputGroup, InputLeftElement, InputRightElement, IconButton, Wrap, WrapItem, Tooltip,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, WarningIcon, CheckCircleIcon, QuestionOutlineIcon } from '@chakra-ui/icons';
import { FaImage, FaWarehouse, FaCheckCircle } from 'react-icons/fa';
import { BadgesStatusVenda, DatasDespacho } from './StatusVendaMeta';

export const AJUDA_ENTRADA_FOTO = 'A peça volta para o estoque e o pedido é marcado com STATUS TAGA CANCELADO. As fotos que você tirar viram a imagem oficial do produto, que volta a ser vendido hoje mesmo.';
export const AJUDA_ENTRADA_ESTOQUE = 'A peça volta para o estoque e o pedido é marcado com STATUS TAGA CANCELADO.';
export const AJUDA_SO_CORRIGIR ='Corrige só a imagem do cadastro: as fotos viram a imagem oficial e o produto volta a ser vendido hoje mesmo. O pedido não é alterado e continua esperando sua decisão: incluir no estoque (pedido fica com STATUS TAGA CANCELADO) ou liberar com ressalva para seguir no despacho.';

const SITUACOES = {
    pendente: { label: 'Aguardando correção da imagem', color: 'purple', icon: WarningIcon },
    corrigida_aguardando: { label: 'Imagem corrigida, aguardando próximo passo', color: 'blue', icon: CheckCircleIcon },
    corrigida_entrada_estoque: { label: 'Imagem corrigida, entrada de estoque', color: 'green', icon: CheckCircleIcon },
    corrigida_ressalva: { label: 'Imagem corrigida, liberado com ressalva', color: 'green', icon: CheckCircleIcon },
};

const AjudaAcao = ({ texto }) => (
    <Tooltip label={texto} hasArrow placement="top" openDelay={200}>
        <IconButton
            aria-label="O que acontece"
            icon={<QuestionOutlineIcon />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={(e) => e.stopPropagation()}
        />
    </Tooltip>
);

const CardDivergente = ({ item, onDarEntrada, onCorrigirImagem }) => {
    const situacao = SITUACOES[item.situacao] || SITUACOES.pendente;
    const pendente = item.situacao === 'pendente';
    // Peca que o cancelamento ja devolveu ao estoque sozinha tambem nao pode
    // receber entrada de novo: era assim que ela entrava em dobro (#2438).
    const vendaCancelada = item.status_taga === 2 || item.estoque_devolvido || !!item.retorno_automatico_em;

    return (
        <Box borderWidth="1px" borderColor={`${situacao.color}.200`} borderRadius="lg" p={3} bg="white" boxShadow="sm">
            <Flex direction={{ base: 'column', md: 'row' }} gap={3} align={{ base: 'stretch', md: 'flex-start' }}>
                <HStack align="flex-start" spacing={3} flex={1} minW={0}>
                    {item.imagem && (
                        <Image src={item.imagem} alt={item.nome} boxSize="56px" objectFit="contain" borderRadius="md" flexShrink={0} />
                    )}
                    <VStack align="flex-start" spacing={1} flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800" whiteSpace="normal" wordBreak="break-word">
                            {item.nome}
                        </Text>
                        <Wrap spacing={1}>
                            <WrapItem><Badge colorScheme="gray">SKU {item.produto_id}</Badge></WrapItem>
                            {item.global_code && <WrapItem><Badge colorScheme="blue" variant="subtle">{item.global_code}</Badge></WrapItem>}
                            {item.marca && <WrapItem><Badge colorScheme="teal" variant="subtle">{item.marca}</Badge></WrapItem>}
                            <WrapItem>
                                <Badge colorScheme={situacao.color} variant="solid" display="flex" alignItems="center" gap="4px">
                                    <Icon as={situacao.icon} boxSize={2.5} />
                                    {situacao.label}
                                </Badge>
                            </WrapItem>
                            {item.bloqueado && (
                                <WrapItem><Badge colorScheme="red" variant="subtle">Produto bloqueado</Badge></WrapItem>
                            )}
                        </Wrap>
                        <BadgesStatusVenda origem={item.origem} status={item.status} statusTaga={item.status_taga} />
                        <DatasDespacho dataVenda={item.data_venda} prazoEnvio={item.prazo_envio} />
                        <Text fontSize="11px" color="gray.500">
                            Venda #{item.id_origem || item.venda_id}
                            {item.cliente ? ` · ${item.cliente}` : ''}
                            {item.problema_at ? ` · marcado em ${new Date(item.problema_at).toLocaleDateString('pt-BR')}` : ''}
                        </Text>
                    </VStack>
                </HStack>

                {(pendente || item.situacao === 'corrigida_aguardando') && !vendaCancelada && (
                    <VStack spacing={2} align="stretch" w={{ base: '100%', md: 'auto' }} minW={{ md: '240px' }}>
                        <HStack spacing={1}>
                            <Button size="sm" colorScheme="orange" leftIcon={<Icon as={FaWarehouse} />} flex={1} onClick={() => onDarEntrada(item)}>
                                {pendente ? 'Entrada + corrigir foto' : 'Dar entrada em estoque'}
                            </Button>
                            <AjudaAcao texto={pendente ? AJUDA_ENTRADA_FOTO : AJUDA_ENTRADA_ESTOQUE} />
                        </HStack>
                        {pendente && (
                            <HStack spacing={1}>
                                <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<Icon as={FaCheckCircle} />} flex={1} onClick={() => onCorrigirImagem(item)}>
                                    Só corrigir foto
                                </Button>
                                <AjudaAcao texto={AJUDA_SO_CORRIGIR} />
                            </HStack>
                        )}
                    </VStack>
                )}

                {pendente && vendaCancelada && (
                    <VStack spacing={2} align="stretch" w={{ base: '100%', md: 'auto' }} minW={{ md: '240px' }}>
                        <HStack spacing={1}>
                            <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<Icon as={FaCheckCircle} />} flex={1} onClick={() => onCorrigirImagem(item)}>
                                Corrigir imagem
                            </Button>
                            <AjudaAcao texto={AJUDA_SO_CORRIGIR} />
                        </HStack>
                    </VStack>
                )}
            </Flex>
        </Box>
    );
};

const SecaoImagemDivergente = ({ itens = [], onDarEntrada, onCorrigirImagem }) => {
    const [busca, setBusca] = useState('');

    const filtrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return itens;
        return itens.filter(i => {
            const campos = [i.nome, i.produto_id, i.global_code, i.marca, i.venda_id, i.id_origem, i.codigo_barra, i.cliente]
                .map(x => String(x ?? '').toLowerCase());
            return campos.some(c => c.includes(termo));
        });
    }, [itens, busca]);

    const pendentes = itens.filter(i => i.situacao === 'pendente').length;

    if (!itens.length) return null;

    return (
        <Box mx={{ base: 2, lg: 6 }} my={4} borderWidth="1px" borderColor="purple.200" borderRadius="lg" overflow="hidden">
            <Flex bg="purple.500" color="white" px={4} py={3} align="center" gap={2} wrap="wrap">
                <Icon as={FaImage} />
                <Text fontWeight="bold">Imagem divergente</Text>
                <Badge bg="whiteAlpha.300" color="white">{filtrados.length} de {itens.length}</Badge>
                {pendentes > 0 && <Badge colorScheme="yellow">{pendentes} aguardando correção</Badge>}
                <Text fontSize="xs" color="whiteAlpha.800" ml={{ base: 0, md: 2 }} w={{ base: '100%', md: 'auto' }}>
                    Itens ficam aqui até a imagem ser corrigida com no mínimo 2 fotos da peça real.
                </Text>
            </Flex>

            <Box p={3} bg="purple.50">
                <InputGroup size="sm" mb={3} maxW="480px">
                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
                    <Input bg="white" borderRadius="md" placeholder="Filtrar por venda, cliente, produto, SKU, global, código..." value={busca} onChange={(e) => setBusca(e.target.value)} />
                    {busca && (
                        <InputRightElement>
                            <IconButton aria-label="Limpar" icon={<CloseIcon boxSize={2.5} />} size="xs" variant="ghost" onClick={() => setBusca('')} />
                        </InputRightElement>
                    )}
                </InputGroup>

                <VStack spacing={3} align="stretch">
                    {filtrados.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>Nenhum item encontrado.</Text>
                    ) : (
                        filtrados.map(i => (
                            <CardDivergente key={i.conferencia_id} item={i} onDarEntrada={onDarEntrada} onCorrigirImagem={onCorrigirImagem} />
                        ))
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default SecaoImagemDivergente;
